import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { SelectField } from '@/components/ui/select';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import {
  use_restaurant_details,
  use_save_restaurant_details,
} from '@/features/restaurant/hooks';
import {
  DELIVERY_RADIUS_OPTIONS,
  FSSAI_REGEX,
  PET_SPECIES_OPTIONS,
  RESTAURANT_SERVICE_OPTIONS,
  RESTAURANT_TYPE_OPTIONS,
  type DeliveryRadius,
  type PetSpecies,
  type RestaurantService,
  type RestaurantType,
} from '@/features/restaurant/types';
import { ChipMultiSelect } from '@/components/chip-multi-select';

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

type Form = {
  restaurant_type: RestaurantType[];
  delivery_radius: DeliveryRadius | '';
  fssai_license_number: string;
  pet_friendly_species: PetSpecies[];
  services_offered: RestaurantService[];
  opening_time: string;
  closing_time: string;
};

const EMPTY: Form = {
  restaurant_type: [],
  delivery_radius: '',
  fssai_license_number: '',
  pet_friendly_species: [],
  services_offered: [],
  opening_time: '09:00',
  closing_time: '22:00',
};

function to_minutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Keys of `Form` whose value is an array — the multi-select pill fields. */
type ArrayKeys<T> = { [K in keyof T]: T[K] extends unknown[] ? K : never }[keyof T];

export function ModuleRestaurantPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_restaurant_details({ enabled: !!draft_q.data });
  const save_m = use_save_restaurant_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    // Vendor selects RESTAURANT at /start; the console routes to the restaurant form.
    if (!draft_q.data.module_types.includes('RESTAURANT')) {
      navigate(next_module_step_after('RESTAURANT', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        restaurant_type: saved_q.data.restaurant_type ?? [],
        delivery_radius: saved_q.data.delivery_radius ?? '',
        fssai_license_number: saved_q.data.fssai_license_number ?? '',
        pet_friendly_species: saved_q.data.pet_friendly_species,
        services_offered: saved_q.data.services_offered,
        opening_time: saved_q.data.opening_time,
        closing_time: saved_q.data.closing_time,
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  /**
   * Toggle a pill *inside* the updater. Computing `toggle(form.x, v)` at the call
   * site reads the render closure, so several picks landing in one React batch
   * all start from the same stale array and only the last one survives.
   */
  function toggle_in<K extends ArrayKeys<Form>>(key: K, value: Form[K][number]) {
    set_form((f) => ({ ...f, [key]: toggle(f[key] as Form[K][number][], value) }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    if (!HH_MM.test(form.opening_time) || !HH_MM.test(form.closing_time)) {
      set_error('Opening and closing times must be in 24h HH:mm format.');
      return;
    }
    if (to_minutes(form.opening_time) >= to_minutes(form.closing_time)) {
      set_error('Opening time must be strictly before closing time.');
      return;
    }
    if (form.pet_friendly_species.length < 1) {
      set_error('Pick at least one pet-friendly species.');
      return;
    }
    if (form.services_offered.length < 1) {
      set_error('Pick at least one service offered.');
      return;
    }

    const fssai = form.fssai_license_number.trim();
    if (fssai !== '' && !FSSAI_REGEX.test(fssai)) {
      set_error('FSSAI license number must be exactly 14 digits.');
      return;
    }

    try {
      await save_m.mutateAsync({
        // Sent even when empty — an empty list is a real answer, and omitting it would
        // leave a previously-saved set in place when a venue clears its selection.
        restaurant_type: form.restaurant_type,
        delivery_radius:
          form.delivery_radius === '' ? undefined : form.delivery_radius,
        fssai_license_number: fssai === '' ? undefined : fssai,
        pet_friendly_species: form.pet_friendly_species,
        services_offered: form.services_offered,
        opening_time: form.opening_time,
        closing_time: form.closing_time,
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('RESTAURANT', draft_q.data.module_types));
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save restaurant details', { highlights_fields: true })
          : 'Could not save restaurant details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-restaurant"
        title="Restaurant details"
        description="Hours, what species you welcome, and how customers can order from you."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-restaurant-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <ChipMultiSelect
          label="Restaurant type"
          hint="pick any that apply"
          testid="restaurant-type"
          options={RESTAURANT_TYPE_OPTIONS}
          selected={form.restaurant_type}
          on_toggle={(v) => toggle_in('restaurant_type', v)}
          disabled={save_m.isPending}
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="delivery_radius">Delivery radius</Label>
            <SelectField
              id="delivery_radius"
              data-testid="field-restaurant-delivery-radius"
              value={form.delivery_radius}
              onValueChange={(v) => update('delivery_radius', v as DeliveryRadius | '')}
              options={[
                { value: '', label: '— No delivery —' },
                ...DELIVERY_RADIUS_OPTIONS,
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="opening_time">Opening time</Label>
            <TimePicker
              id="opening_time"
              name="opening_time"
              data-testid="field-restaurant-opening-time"
              value={form.opening_time}
              onChange={(v) => update('opening_time', v)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="closing_time">Closing time</Label>
            <TimePicker
              id="closing_time"
              name="closing_time"
              data-testid="field-restaurant-closing-time"
              value={form.closing_time}
              onChange={(v) => update('closing_time', v)}
            />
          </div>
        </div>

        <TextField
          label="FSSAI license number"
          name="fssai_license_number"
          error={field_errors.fssai_license_number}
          data-testid="field-restaurant-fssai"
          value={form.fssai_license_number}
          onChange={(e) =>
            update(
              'fssai_license_number',
              e.target.value.replace(/\D/g, '').slice(0, 14),
            )
          }
          inputMode="numeric"
          placeholder="12345678901234"
          optional
          hint="14 digits. Upload the certificate separately under Documents & photos."
        />

        <div className="flex flex-col gap-2">
          <Label>Pet-friendly species (pick 1–6)</Label>
          <div className="flex flex-wrap gap-2" data-testid="restaurant-species">
            {PET_SPECIES_OPTIONS.map((s) => {
              const active = form.pet_friendly_species.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`restaurant-species-${s.value}`}
                  data-checked={active}
                  onClick={() =>
                    toggle_in('pet_friendly_species', s.value)
                  }
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Services offered (pick 1–4)</Label>
          <div className="flex flex-wrap gap-2" data-testid="restaurant-services">
            {RESTAURANT_SERVICE_OPTIONS.map((s) => {
              const active = form.services_offered.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`restaurant-service-${s.value}`}
                  data-checked={active}
                  onClick={() =>
                    toggle_in('services_offered', s.value)
                  }
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </StepSection>
    </form>
  );
}
